<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSysStore } from '@/store'
import { useRaceAnswerNotifyModal } from '@/hooks/useRaceAnswerNotifyModal'

const router = useRouter()
const { t } = useI18n()
const sysStore = useSysStore()
const { raceAnswerNotifyState, closeRaceAnswerNotifyModal } = useRaceAnswerNotifyModal()

const payload = computed(() => raceAnswerNotifyState.payload)
const isRush = computed(() => payload.value?.taskType === 'RUSH')
const isSmallScreen = computed(() => sysStore.isSmallScreen)

const raceAnswerNotifyModalZIndex = 2000

const handleGoRaceAnswer = () => {
  if (!payload.value) return
  router.push({
    path: '/task/race-answer',
    query: {
      taskId: String(payload.value.taskId),
      taskType: payload.value.taskType,
      classId: String(payload.value.classId)
    }
  })
  closeRaceAnswerNotifyModal()
}
</script>

<template>
  <a-modal
    :visible="raceAnswerNotifyState.visible"
    :footer="false"
    :mask-closable="false"
    :style="{
      zIndex: raceAnswerNotifyModalZIndex
    }"
    :width="isSmallScreen ? '100%' : '440px'"
    :class="isSmallScreen ? 'race-answer-notify-mobile' : 'race-answer-notify'"
    :modal-style="isSmallScreen ? { background: 'transparent', boxShadow: 'none', overflow: 'visible' } : {}"
    :body-style="isSmallScreen ? { padding: 0, background: 'transparent', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' } : {}"
    :title="!isSmallScreen && isRush ? t('task.callAndRush.rushAlert') : ''"
    @cancel="closeRaceAnswerNotifyModal"
  >
    <!-- 抢答 (RUSH) -->
    <template v-if="isRush && payload">
      <div v-if="isSmallScreen" class="race-answer-notify-card-mobile">
        <h3 class="race-answer-notify-title">
          {{ t('task.callAndRush.rushAlert') }}
        </h3>
        <p class="race-answer-notify-subtitle">
          <span v-html="t('task.callAndRush.countdownStarted', { courseName: `<span class='race-answer-notify-highlight'>${payload.courseName}</span>`, roundNum: payload.roundNum })"></span>
        </p>
        <div class="race-answer-notify-content">
          {{ payload.content }}
        </div>
        <div class="race-answer-notify-rush-footer">
          <a-button
            type="primary"
            size="large"
            class="race-answer-notify-btn race-answer-notify-btn--primary"
            @click="handleGoRaceAnswer"
          >
            {{ t('task.callAndRush.goToRush') }}
          </a-button>
          <button
            class="race-answer-notify-btn race-answer-notify-btn--cancel"
            type="button"
            @click="closeRaceAnswerNotifyModal"
          >
            {{ t('task.callAndRush.cancel') }}
          </button>
        </div>
      </div>
      <div v-else class="race-answer-notify-pc">
        <p class="race-answer-notify-subtitle">
          <span v-html="t('task.callAndRush.countdownStarted', { courseName: `<span class='race-answer-notify-highlight'>${payload.courseName}</span>`, roundNum: payload.roundNum })"></span>
        </p>
        <div class="race-answer-notify-content">
          {{ payload.content }}
        </div>
        <div class="race-answer-notify-rush-footer">
          <a-button
            size="large"
            class="race-answer-notify-btn race-answer-notify-btn--cancel"
            @click="closeRaceAnswerNotifyModal"
          >
            {{ t('task.callAndRush.cancel') }}
          </a-button>
          <a-button
            type="primary"
            size="large"
            class="race-answer-notify-btn race-answer-notify-btn--primary"
            @click="handleGoRaceAnswer"
          >
            {{ t('task.callAndRush.goToRush') }}
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<style lang="less">
.race-answer-notify-mobile {
  .arco-modal-header {
    display: none !important;
  }
  .arco-modal-body {
    background: transparent !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .arco-modal-content {
    background: transparent !important;
    box-shadow: none !important;
    overflow: visible !important;
  }
}

.race-answer-notify {
  .arco-modal-header {
    padding: 18px 24px 16px;
    border-bottom: 1px solid #F2F3F5;
    text-align: center;
  }
  .arco-modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #1D2129;
  }
  .arco-modal-body {
    padding: 24px 16px 14px;
  }
}
</style>

<style scoped lang="less">
.race-answer-notify-pc {
  text-align: center;
  padding: 8px 0;
}

.race-answer-notify-pc .race-answer-notify-rush-footer {
  flex-direction: row;
  justify-content: center;
  padding: 16px 0 0;
  border-top: 1px solid #F2F3F5;
}

.race-answer-notify-pc .race-answer-notify-rush-footer .race-answer-notify-btn--cancel {
  width: 132px;
  height: 44px;
  border-radius: 8px;
  background: #F5F7FA;
  border: 1px solid #F5F7FA;
  color: #4E5969;
  font-size: 18px;
  line-height: 44px;
  padding: 0;
  &:hover {
    background: #E8EAED;
    color: #4E5969;
  }
}

.race-answer-notify-pc .race-answer-notify-rush-footer .race-answer-notify-btn--primary {
  width: 132px;
  height: 44px;
  border-radius: 8px;
  font-size: 18px;
}

.race-answer-notify-card-mobile {
  width: min(311px, calc(100vw - 88px));
  margin: 0 auto;
  border-radius: 32px;
  background: #FFFFFF;
  box-shadow: 0 20px 60px rgba(15, 35, 95, 0.16);
  padding: 44px 32px 26px;
  text-align: center;
  box-sizing: border-box;
}

.race-answer-notify-title {
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
  color: #2F3C69;
  margin: 0 0 20px;
}

.race-answer-notify-subtitle {
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  color: #3C466D;
  margin: 0 0 20px;
}

.race-answer-notify-highlight {
  color: #3E94FF;
}

.race-answer-notify-content {
  font-size: 15px;
  line-height: 24px;
  color: #596483;
  margin-bottom: 28px;
  word-break: break-word;
}

.race-answer-notify-rush-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.race-answer-notify-btn {
  min-width: 132px;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;

  &--primary {
    width: 100%;
    height: 52px;
    border-radius: 16px;
    border: none;
  }

  &--cancel {
    border: none;
    background: transparent;
    padding: 0;
    color: #C4C7D1;
    font-size: 16px;
    line-height: 24px;
    min-width: auto;
    height: auto;

    &:hover {
      background: transparent;
      color: #8D96A8;
    }
  }
}
</style>
